import React, { useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/router';
import { useViewport } from 'hooks/3catinfo/useViewport';
import { getUrlFriendly } from 'components/3catinfo/Enllac/RouteResolver';
import clsx from 'clsx';
import Boto from 'components/3catinfo/Boto';
import Enllac from 'components/3catinfo/Enllac';
import __isEmpty from 'lodash/isEmpty';
import Icona from '../ui/Icona';
import Modal from './Modal';
import styles from './compartir.module.scss';

const Compartir = ({
    className,
    mida = '20',
    midaBoto,
    estil,
    estils,
    titol = '',
    mostraTwitter = true,
    mostraWhatsapp = true,
    mostraFacebook = true,
    mostraTelegram = true,
    mostraLinkedIn = true,
    mostraEnllac = true,
    route,
    obreEsquerra = false,
    titolCompartirMobil = '',
    textTooltip = ''
}) => {
    const arrayEstils = estils?.split(' ') || [];
    const router = useRouter();
    const { isMobileDevice, zoomNavigator, isMobile, isOnlyMobile } = useViewport();
    const [showModal, setShowModal] = useState(false);
    const [hoverModal, setHoverModal] = useState(false);
    const [isTancat, setIsTancat] = useState(true);
    const [isModalInteractive, setIsModalInteractive] = useState(false);
    const [isCopyingLink, setIsCopyingLink] = useState(false);
    const timerCopyLink = useRef(null);
    const modalRef = useRef(null);
    const compartirBtnRef = useRef(null);

    const stylesCompartir = clsx({
        [styles.compartir]: true,
        [styles[estil]]: !!estil,
        [styles.esquerra]: obreEsquerra,
        [className]: !__isEmpty(className),
        [styles[midaBoto]]: !!midaBoto,
        ...arrayEstils.reduce((acc, currentStyle) => {
            acc[styles[currentStyle]] = !__isEmpty(currentStyle);
            return acc;
        }, {})
    });

    const stylesBtnCopiarEnllac = clsx({
        [styles.btn_copia]: true,
        [styles.copiant]: isCopyingLink
    });

    const stylesModalOverlay = clsx({
        [styles.overlay]: true,
        [styles.tancat]: isTancat,
        [styles.interactiu]: isModalInteractive,
        [styles.overlayMobile]: isMobile,
        [styles.ambZoom400]: zoomNavigator > 3.5
    });

    const stylesOverlay = clsx({
        [styles.overlayTooltip]: true,
        [styles.dretaTooltip]: !obreEsquerra && !isMobile,
        [styles.esquerraTooltip]: obreEsquerra && !isMobile,
        [styles.negatiu]: estil === 'negatiu' && !isMobile,
        [styles.leftModal]: !!className,
        [styles.forMobile]: isMobile
    });

    const toggleModalHandler = async (url) => {
        if (navigator.share && isMobileDevice) {
            try {
                await navigator.share({
                    title: titolCompartirMobil,
                    text: titolCompartirMobil,
                    url
                });
            } catch (error) {
                console.error('Error al compartir:', error);
            }
        } else {
            if (!showModal) {
                setShowModal(true);
                setHoverModal(false);
                if (isOnlyMobile) document.body.style.overflow = 'hidden';
                setTimeout(() => setIsTancat(false), 0);
                setTimeout(() => setIsModalInteractive(true), 300);
            } else {
                setIsTancat(true);
                setIsModalInteractive(false);
                if (isOnlyMobile) document.body.style.overflow = 'auto';
                setTimeout(() => setShowModal(false), 300);
            }
        }
    };

    const handleClickCopyLink = () => {
        if (!isCopyingLink) {
            navigator.clipboard.writeText(url);
            setIsCopyingLink(true);
            timerCopyLink.current = null;
            timerCopyLink.current = setTimeout(() => {
                setIsCopyingLink(false);
            }, 5000);
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Escape' && showModal) {
            toggleModalHandler();
        }
        if (e.key === 'Tab') {
            // Controla que el focus no surti del dropdown amb el Tab
            handleFocus(e);
        }
    };

    const handleFocus = (event) => {
        if (showModal) {
            const focusableElem = Array.from(modalRef.current.querySelectorAll("a, button, [tabindex]:not([tabindex='-1'])"));

            const firstFocusableElem = focusableElem[0];
            const lastFocusableElem = focusableElem[focusableElem.length - 1];
            const isTabPressed = event.key === 'Tab';

            if (!isTabPressed) {
                return;
            }

            if (event.shiftKey) {
                if (document.activeElement === firstFocusableElem) {
                    lastFocusableElem.focus();
                    event.preventDefault();
                }
            } else if (document.activeElement === lastFocusableElem) {
                firstFocusableElem.focus();
                event.preventDefault();
            }
        }
    };

    const _getCanal = (canals) => {
        const c = canals.canal ? canals.canal : canals;
        const canalMain = c.find((canal) => canal.main === true);
        return canalMain?.desc || c?.[0]?.desc || 'sense';
    };

    const _getUrl = (route) => {
        if (route.href) return route.href;
        const canalMain = route?.canals ? _getCanal(route.canals) : 'sense';
        return getUrlFriendly({ ...route, routePath: route?.type, canal: canalMain, producte: 'web_noticies' });
    };

    const titolAudiencies = !__isEmpty(titol) ? titol.replace(':', '.') : route?.slug;

    const url = __isEmpty(route) ? 'https://www.3cat.cat' + router.asPath : 'https://www.3cat.cat' + _getUrl(route);

    const hitAudiencies = (type) => {
        const xarxa = type || 'XS desconeguda';
        _satellite?.track('scCompartir', { xarxaAccioContingut: xarxa + titolAudiencies });
    };

    useEffect(() => {
        if (!showModal) {
            timerCopyLink.current = null;
            setIsCopyingLink(false);
        } else {
            window.addEventListener('keydown', handleKeyDown);
        }

        return () => {
            window.removeEventListener('keydown', handleKeyDown);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showModal]);

    return (
        <div className={stylesCompartir} data-testid={'boto-compartir'}>
            <button
                className={styles.boto}
                onClick={() => {
                    toggleModalHandler(url);
                }}
                onMouseEnter={() => {
                    {
                        !showModal && setHoverModal(true);
                    }
                }}
                onFocus={() => {
                    {
                        !showModal && setHoverModal(true);
                    }
                }}
                onMouseLeave={() => {
                    setHoverModal(false);
                }}
                onBlur={() => setHoverModal(false)}
                aria-expanded={showModal}
                aria-label='Comparteix'
                ref={compartirBtnRef}
                data-type='compartir'
            >
                <Icona
                    inline={true}
                    mida={mida}
                    className={styles.icona}
                    isBlanc={estil === 'negatiu' || arrayEstils.includes('negatiu')}
                    icona={showModal ? 'tanca' : 'compartir'}
                    alt='Compartir'
                />
            </button>
            <div ref={modalRef}>
                <Modal isOpen={showModal}>
                    <ul className={stylesModalOverlay}>
                        {mostraEnllac && (
                            <li>
                                <Boto
                                    tag='button'
                                    text={isCopyingLink ? 'Copiat!' : "Copia l'enllaç"}
                                    estil='transparent'
                                    icona={isCopyingLink ? 'checked_contorn_cercle' : 'enllac'}
                                    isIconaDreta={false}
                                    isIconaBlanc={true}
                                    iconaInline={true}
                                    mida={24}
                                    className={stylesBtnCopiarEnllac}
                                    fons={estil !== 'negatiu' || 'fonsContenidorNegre'}
                                    onClick={handleClickCopyLink}
                                />
                            </li>
                        )}
                        {mostraWhatsapp && (
                            <li>
                                <Enllac
                                    href={'https://wa.me/?text=' + url + '?ext=SM_WP_F4_CE24_'}
                                    onClick={() => {
                                        hitAudiencies('Whatsapp:share:');
                                    }}
                                    className={styles.link}
                                    data-action='share/whatsapp/share'
                                    target='_blank'
                                >
                                    <Icona
                                        icona='whatsappGran'
                                        inline={true}
                                        isBlanc={true}
                                        className={styles.iconaSocial}
                                        alt='Whatsapp'
                                        role='img'
                                    />
                                </Enllac>
                            </li>
                        )}
                        {mostraTwitter && (
                            <li>
                                <button
                                    onClick={() => {
                                        const cadena =
                                            'https://x.com/intent/tweet?text=' + titol + '&url=' + url + '?ext=SM_TW_F4_CE24_';
                                        window.open(cadena, 'Twitter', 'toolbar=0, status=0, width=550, height=500');
                                        hitAudiencies('Twitter:share:');
                                    }}
                                    href={'https://x.com/intent/tweet?text=' + titol + '&url=' + url}
                                    className={styles.link}
                                >
                                    <Icona
                                        icona='x'
                                        inline={true}
                                        isBlanc={true}
                                        className={styles.iconaSocial}
                                        alt='X'
                                        role='img'
                                    />
                                </button>
                            </li>
                        )}
                        {mostraFacebook && (
                            <li>
                                <button
                                    onClick={() => {
                                        const cadena =
                                            'https://www.facebook.com/sharer/sharer.php?u=' + url + '?ext=SM_FB_F4_CE24_';
                                        window.open(cadena, 'Facebook', 'toolbar=0, status=0, width=550, height=500');
                                        hitAudiencies('Facebook:share:');
                                    }}
                                    className={styles.link}
                                >
                                    <Icona
                                        icona='facebook_rodona'
                                        inline={true}
                                        isBlanc={true}
                                        className={styles.iconaSocial}
                                        alt='Facebook'
                                        role='img'
                                    />
                                </button>
                            </li>
                        )}
                        {mostraTelegram && (
                            <li>
                                <button
                                    onClick={() => {
                                        const cadena =
                                            'https://t.me/share/url?text=' + titol + '&url=' + url + '?ext=SM_TG_F4_CE24_';
                                        window.open(cadena, 'Telegram', 'toolbar=0, status=0, width=550, height=500');
                                        hitAudiencies('Telegram:share:');
                                    }}
                                    className={styles.link}
                                >
                                    <Icona
                                        icona='telegram_rodona'
                                        inline={true}
                                        isBlanc={true}
                                        className={styles.iconaSocial}
                                        alt='Telegram'
                                        role='img'
                                    />
                                </button>
                            </li>
                        )}
                        {mostraLinkedIn && (
                            <li>
                                <button
                                    onClick={() => {
                                        const cadena =
                                            'https://www.linkedin.com/shareArticle?mini=true&summary=' +
                                            titol +
                                            '&url=' +
                                            url +
                                            '?ext=SM_LKD_F4_CE24_';
                                        window.open(cadena, 'LinkedIn', 'toolbar=0, status=0, width=550, height=500');
                                        hitAudiencies('LinkedIn:share:');
                                    }}
                                    className={styles.link}
                                >
                                    <Icona
                                        icona='linkedin'
                                        inline={true}
                                        isBlanc={true}
                                        className={styles.iconaSocial}
                                        alt='LinkedIn'
                                        role='img'
                                    />
                                </button>
                            </li>
                        )}
                        {isMobile && (
                            <button
                                className={styles.tancarModal}
                                onClick={() => {
                                    toggleModalHandler(url);
                                    setHoverModal(false);
                                }}
                            >
                                <Icona
                                    className={styles.iconaTancarModal}
                                    icona='tanca'
                                    alt='tanca'
                                    mida='9'
                                    isBlanc={true}
                                    inline={true}
                                />
                            </button>
                        )}
                    </ul>
                </Modal>
                {textTooltip && (
                    <Modal isOpen={hoverModal}>
                        <div className={stylesOverlay}>{textTooltip}</div>
                    </Modal>
                )}
            </div>
        </div>
    );
};

export default React.memo(Compartir);
